<template>
  <el-config-provider :locale="locale">
    <div class="jcal">
      <div class="jcal__header">
        <h1 class="jcal__title">Calendario Percorsi</h1>
        <el-date-picker
          v-model="selectedMonth"
          type="month"
          :format="monthFormat"
          placeholder="Seleziona mese e anno"
          :clearable="false"
          @change="onMonthChange"
        />
      </div>

      <div class="jcal__card">
        <el-calendar v-model="selectedDate" :default-value="selectedMonth">
          <template #date-cell="{ data }">
            <div
              class="jcal__day"
              :class="{
                'jcal__day--journey': getJourneysForDay(data.day).length > 0,
                'jcal__day--today': isToday(data.day),
              }"
              @click="openDetailsPage(data.day, null)"
            >
              <span class="jcal__num">{{ formatDate(data.day) }}</span>

              <div
                v-if="getJourneysForDay(data.day).length"
                class="jcal__pills"
              >
                <button
                  v-for="journey in getJourneysForDay(data.day)"
                  :key="journey.id"
                  type="button"
                  class="jcal__pill"
                  @click.stop="openDetailsPage(data.day, journey.id)"
                >
                  {{ journey.corse }} {{ journey.corse === 1 ? 'corsa' : 'corse' }}
                </button>
              </div>
              <span v-else class="jcal__add" aria-hidden="true">+</span>
            </div>
          </template>
        </el-calendar>
      </div>

      <div class="jcal__legend">
        <span class="jcal__legend-dot"></span> Giorni con percorsi ·
        clicca un giorno vuoto per crearne uno nuovo
      </div>
    </div>
  </el-config-provider>
</template>

<script>
import { ref } from 'vue';
import journeyAPI from '../services/journeyAPI';
import { isLoggedStore } from '../store/logged';
import { ElCalendar, ElConfigProvider } from 'element-plus';
import { useRouter } from 'vue-router';
import 'element-plus/dist/index.css';
import it from 'element-plus/es/locale/lang/it';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
dayjs.locale('it');

export default {
  name: 'JourneyCalendar',
  components: {
    ElCalendar,
    ElConfigProvider,
  },
  setup() {
    const storeLogin = isLoggedStore();
    const selectedDate = ref(new Date());
    const selectedMonth = ref(null);
    const router = useRouter();
    const journeys = ref([]);
    const locale = it;
    const monthFormat = 'MMMM YYYY';

    const getJourneys = async () => {
      try {
        const response = await journeyAPI.getJourneys(storeLogin.partner.id);
        journeys.value = response.data.map((journey) => ({
          id: journey.id,
          date: dayjs(journey.date).format('YYYY-MM-DD'),
          // Numero di corse della giornata.
          corse: journey.routes?.length || 0,
        }));
      } catch (error) {
        console.error('Errore nel recuperare i journey:', error.message);
      }
    };

    const getJourneysForDay = (day) =>
      journeys.value.filter((journey) => journey.date === day);

    const formatDate = (day) => dayjs(day).format('DD');

    // Evidenzia il giorno corrente nel calendario.
    const isToday = (day) => day === dayjs().format('YYYY-MM-DD');

    getJourneys();

    const openDetailsPage = (day, id) => {
      const formattedDay = dayjs(day).format('YYYY-MM-DD');
      const dayJourneys = getJourneysForDay(formattedDay);

      if (dayJourneys.length > 0) {
        // Click sulla cella (id null): apri il primo journey del giorno.
        router.push({
          name: 'DetailsJourneyPage',
          params: { date: formattedDay, id: id || dayJourneys[0].id },
        });
      } else {
        router.push({ name: 'JourneyPage', params: { date: formattedDay } });
      }
    };

    const onMonthChange = (date) => {
      selectedMonth.value = dayjs(date).locale('it').format('MMMM YYYY');
      selectedDate.value = dayjs(date).startOf('month').toDate();
    };

    return {
      selectedDate,
      journeys,
      formatDate,
      openDetailsPage,
      locale,
      onMonthChange,
      selectedMonth,
      monthFormat,
      getJourneysForDay,
      isToday,
    };
  },
};
</script>

<style>
/* =========================================================================
   Calendario percorsi — coerente col design system Journey.
   Token brand: #4b30f2 (primary), #dcf130 (accent).
   ========================================================================= */
.jcal {
  --jcal-primary: #4b30f2;
  --jcal-primary-soft: rgba(75, 48, 242, 0.08);
  --jcal-accent: #dcf130;
  --jcal-text: #0f172a;
  --jcal-muted: #475569;
  --jcal-border: #e6e8ef;

  min-height: 100vh;
  margin: -20px;
  padding: 28px clamp(16px, 4vw, 48px) 48px;
  background: #f4f5f7;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    sans-serif;
  color: var(--jcal-text);
}

.jcal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.jcal__title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.jcal__card {
  background: #ffffff;
  border-radius: 18px;
  padding: 12px 16px 16px;
  box-shadow: 0 1px 2px rgba(16, 16, 20, 0.04),
    0 10px 30px rgba(16, 16, 20, 0.05);
}

/* Ripulisce il frame di Element Plus */
.jcal .el-calendar {
  background: transparent;
  --el-calendar-border: none;
}
.jcal .el-calendar__header {
  border-bottom: 1px solid var(--jcal-border);
}
.jcal .el-calendar-table td {
  border-color: var(--jcal-border);
}
.jcal .el-calendar-table .el-calendar-day {
  height: auto;
  min-height: 88px;
  padding: 0;
}
.jcal .el-calendar-table td.is-selected {
  background: transparent;
}
.jcal .el-calendar-table .el-calendar-day:hover {
  background: var(--jcal-primary-soft);
}

/* Cella giorno */
.jcal__day {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 88px;
  padding: 8px 6px;
  box-sizing: border-box;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

/* Giorno CON journey: evidenziato con tint + barra superiore + numero cerchiato */
.jcal__day--journey {
  background: var(--jcal-primary-soft);
  box-shadow: inset 0 3px 0 var(--jcal-primary);
}
.jcal__num {
  font-size: 14px;
  font-weight: 700;
  color: var(--jcal-text);
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}
.jcal__day--journey .jcal__num {
  background: var(--jcal-primary);
  color: #fff;
}

/* Giorno CORRENTE: anello brand attorno alla cella + numero su lime */
.jcal__day--today {
  box-shadow: inset 0 0 0 2px var(--jcal-primary);
  border-radius: 10px;
}
.jcal__day--today.jcal__day--journey {
  box-shadow: inset 0 0 0 2px var(--jcal-primary),
    inset 0 3px 0 var(--jcal-primary);
}
.jcal__day--today .jcal__num {
  background: var(--jcal-accent);
  color: #1a1a1a;
}
.jcal__day--today.jcal__day--journey .jcal__num {
  background: var(--jcal-primary);
  color: #fff;
  box-shadow: 0 0 0 2px var(--jcal-accent);
}

/* Pillola: numero di corse della giornata */
.jcal__pills {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  align-items: center;
}
.jcal__pill {
  border: none;
  background: var(--jcal-accent);
  color: #1a1a1a;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.18s ease;
}
.jcal__pill:hover {
  background: var(--jcal-primary);
  color: #fff;
}

/* Hint "+" sui giorni vuoti (visibile all'hover) */
.jcal__add {
  opacity: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--jcal-primary);
  transition: opacity 0.18s ease;
}
.jcal__day:hover .jcal__add {
  opacity: 1;
}

.jcal__legend {
  margin-top: 14px;
  font-size: 13px;
  color: var(--jcal-muted);
  display: flex;
  align-items: center;
  gap: 8px;
}
.jcal__legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 4px;
  background: var(--jcal-primary-soft);
  box-shadow: inset 0 2px 0 var(--jcal-primary);
}
</style>
